// src/context/AuthContext.js
import React, { createContext, useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { getApiBaseUrl, setupAxiosAuth } from '../utils/urlConfig';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [loginError, setLoginError] = useState('');
  // Drives which pages the shell renders. Held in state, not localStorage, so
  // it always comes from a server-verified token rather than something the
  // browser can edit — the server enforces the same split regardless.
  const [role, setRole] = useState(null);

  // Get API base URL from centralized configuration
  const API_BASE_URL = getApiBaseUrl();
  
  useEffect(() => {
    const verifyAuth = async () => {
      setIsAuthLoading(true);
      
      const token = localStorage.getItem('token');
      if (!token) {
        setIsAuthenticated(false);
        setRole(null);
        setIsAuthLoading(false);
        return;
      }
      
      try {
        // Set auth header using helper function
        setupAxiosAuth(axios);
        
        // Verify token
        const res = await axios.get(`${API_BASE_URL}/auth/verify`);
        
        if (res.data.isValid) {
          // get user data from the response
          const userData = res.data.user;
          // set the user data in localstorage securely
          localStorage.setItem('username', JSON.stringify(userData.username));
          setRole(userData.role);
          setIsAuthenticated(true);
          setLoginError('');
        } else {
          localStorage.removeItem('token');
          localStorage.removeItem('username');
          setIsAuthenticated(false);
          setRole(null);
        }
      } catch (err) {
        console.error('Auth verification error:', err);
        localStorage.removeItem('token');
        localStorage.removeItem('username');
        setIsAuthenticated(false);
        setRole(null);
      } finally {
        setIsAuthLoading(false);
      }
    };
    
    verifyAuth();
  }, [API_BASE_URL]);
  
  const login = async (credentials) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/login`, credentials);
      const { token, user } = response.data;

      localStorage.setItem('token', token);
      if (user?.username) {
        localStorage.setItem('username', JSON.stringify(user.username));
      }
      setupAxiosAuth(axios);

      setRole(user?.role ?? null);
      setIsAuthenticated(true);
      setLoginError('');
      return true;
    } catch (error) {
      console.error(error);
      const errorMessage = error.response?.data?.message || 'Login failed';
      setLoginError(errorMessage);
      toast.error(errorMessage);
      return false;
    }
  };
  
  const verifyToken = async () => {
    try {
      // take the token from localStorage
      const token = localStorage.getItem('token');
      if (!token) return false;
      
      // Set auth header using helper function
      setupAxiosAuth(axios);
      
      // Verify token
      const response = await axios.get(`${API_BASE_URL}/auth/verify`);
      // get user data from the response
      const isValid = response.data.isValid;
      // if the token is valid, set the user data in the state
      if (isValid) {
        // set the user data in localstorage securely
        localStorage.setItem('username', JSON.stringify(response.data.user.username));
        setRole(response.data.user.role);
        setIsAuthenticated(true);
      } else {
        setIsAuthenticated(false);
        setRole(null);
      }
      return response.data.isValid;
    } catch (error) {
      console.error('Token verification error:', error);
      return false;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('username');
    delete axios.defaults.headers.common['x-auth-token'];
    setIsAuthenticated(false);
    setRole(null);
    toast.info('You have been logged out');
  };
  
  return (
    <AuthContext.Provider value={{
      isAuthenticated,
      isAuthLoading,
      loginError,
      role,
      login,
      verifyToken,
      logout
    }}>
      {children}
    </AuthContext.Provider>
  );
};